import * as React from 'react';
import styled from '@emotion/styled';
import H3 from '@base/H3/H3';
import P1 from '@base/P1/P1';
import { Fonts, Spacings } from '@styles/index';
import useFetch from '@hooks/useFetch';
import Button from '@base/Button/Button';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import { submitFeedback } from '@api/fetchers/batch';
import { segment } from '@actions/analytics';
import {
  BATCH_ENROLL_FEEDBACK_CLICKED,
  BATCH_UNENROLL_FEEDBACK_CLICKED
} from '@constants/segment';
import useGoalEventProps from '@hooks/useGoalEventProps';
import useAllTypesOfCourseEventProps from '@hooks/useAllTypesOfCourseEventProps';
import useBatchDetails from '@api/hooks/batch/useBatchDetails';
import BatchOptionsList from './BatchOptionsList';

interface IBatchFeedbackDrawerProps {
  open: boolean;
  ctaText: string;
  endpoint: string;
  batchUID: string;
  onCtaClick: () => void;
  drawerType: string;
}

interface IHide {
  $hide: boolean;
}

const StyH3 = styled(H3)`
  grid-column: span 10;
  font-weight: ${Fonts.HEAVY};
  line-height: 28px;
`;

const BottomWrapper = styled.div`
  position: fixed;
  bottom: 0;
  height: ${Spacings.SPACING_18B};

  margin-left: -48px;
  display: grid;
  background-color: var(--color-base-1);
  padding-left: ${Spacings.SPACING_12B};
  padding-right: 115px;
  grid-template-columns: 3fr 1fr;
  justify-content: space-between;
  align-items: center;
  box-shadow: ${boxShadowGenerator({
    yOffset: '-16px',
    blur: Spacings.SPACING_8B
  })};
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const StyP1 = styled(P1)<IHide>`
  font-weight: ${Fonts.SEMIBOLD};
  line-height: 18px;
  opacity: ${({ $hide }) => ($hide ? 1 : 0)};
`;

export const drawerTypes = {
  JOIN_BATCH: 'JOIN_BATCH',
  LEAVE_BATCH: 'LEAVE_BATCH',
  CONFIRMATION: 'CONFIRMATION',
  SUCCESS: 'SUCCESS'
};

const BatchFeedbackDrawer: React.FC<IBatchFeedbackDrawerProps> = ({
  open,
  ctaText,
  endpoint,
  onCtaClick,
  batchUID,
  drawerType
}) => {
  const {
    data = {},
    mutate: refetchOptions,
    isLoading
  } = useFetch(open ? endpoint : null);
  const { choices = [], description = '', uid } = data;
  const { options = [], id } = choices[0] || {};

  const [error, setError] = React.useState(false);
  const [selectedOptions, setSelectedOptions] = React.useState([]);
  const [otherReason, setOtherReason] = React.useState('');
  const [disable, setDisable] = React.useState(false);

  // @ts-ignore
  const goalSegmentData = useGoalEventProps({ batchGoalUID: batchUID });
  const { batchDetails } = useBatchDetails(batchUID);
  const segmentData = useAllTypesOfCourseEventProps({
    course: batchDetails,
    isBatch: true
  });

  React.useEffect(() => {
    if (open) refetchOptions();
    setSelectedOptions([]);
    setError(false);
    setDisable(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (isLoading) return null;

  const onClick = async () => {
    if (!selectedOptions.length) setError(true);
    else {
      setDisable(true);
      const result = options.map((option) => {
        const obj = { ...option };
        obj.value = selectedOptions.includes(obj.id) ? 1 : 0;
        return obj;
      });
      await submitFeedback({
        batchUID,
        feedbackUID: uid,
        choiceUID: id,
        options: result,
        otherReason
      });

      const feedbackArray = result
        .filter((option) => !!option.value)
        .map((option) => option.name);
      const feedback = feedbackArray.join(';');

      segment.track(
        drawerType === drawerTypes.LEAVE_BATCH
          ? BATCH_UNENROLL_FEEDBACK_CLICKED
          : BATCH_ENROLL_FEEDBACK_CLICKED,
        {
          ...goalSegmentData,
          ...segmentData,
          feedback_comments: otherReason,
          feedback_type: feedback
        }
      );
      onCtaClick();
    }
  };

  const onOptionsSelect = (selected) => {
    setSelectedOptions(selected);
    setError(false);
  };

  const onDescriptionChange = (e) => setOtherReason(e.target.value);

  if (!open) return null;

  return (
    <>
      <StyH3 color="var(--color-text-primary)">{description}</StyH3>
      <BatchOptionsList
        options={options}
        selected={selectedOptions}
        onOptionsSelect={onOptionsSelect}
        onDescriptionChange={onDescriptionChange}
      />
      <BottomWrapper>
        <StyP1 color="var(--color-i-red)" $hide={error}>
          Choose at least one reason to continue
        </StyP1>
        <Button
          // @ts-ignore
          label={ctaText}
          color="black"
          variant={disable ? 'disabled' : 'filled'}
          width="136px"
          maxHeight="sm"
          grid={2}
          onClick={onClick}
        />
      </BottomWrapper>
    </>
  );
};

export default BatchFeedbackDrawer;
